import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { tenantsApi, type Tenant } from "@/api/tenants";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Dialog } from "@/components/ui/Dialog";

export function AdminTenants() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["tenants"], queryFn: tenantsApi.list });
  const [openNew, setOpenNew] = useState(false);

  const updateMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Tenant["status"] }) =>
      tenantsApi.update(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">Empresas (tenants)</h1>
          <Button onClick={() => setOpenNew(true)}>
            <Plus className="size-4" /> Nueva empresa
          </Button>
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Empresa</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Plan</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-500">Cargando...</td>
                </tr>
              )}
              {data?.map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium">{t.name}</td>
                  <td className="px-4 py-2 text-slate-500">{t.slug}</td>
                  <td className="px-4 py-2">{t.plan}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      {t.status !== "active" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => updateMut.mutate({ id: t.id, status: "active" })}
                        >
                          Activar
                        </Button>
                      )}
                      {t.status === "active" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => updateMut.mutate({ id: t.id, status: "muted" })}
                        >
                          Pausar
                        </Button>
                      )}
                      {t.status !== "suspended" && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => updateMut.mutate({ id: t.id, status: "suspended" })}
                        >
                          Suspender
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <NewTenantDialog open={openNew} onClose={() => setOpenNew(false)} />
    </div>
  );
}

function StatusBadge({ status }: { status: Tenant["status"] }) {
  const colors: Record<Tenant["status"], string> = {
    active: "bg-emerald-100 text-emerald-800",
    muted: "bg-amber-100 text-amber-800",
    suspended: "bg-red-100 text-red-800",
    deleted: "bg-slate-200 text-slate-700",
  };
  const labels: Record<Tenant["status"], string> = {
    active: "Activa",
    muted: "Pausada",
    suspended: "Suspendida",
    deleted: "Eliminada",
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[status]}`}>
      {labels[status]}
    </span>
  );
}

function NewTenantDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: "",
    slug: "",
    admin_email: "",
    admin_full_name: "",
    admin_password: "",
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => tenantsApi.create({ ...form, plan: "standard" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setForm({ name: "", slug: "", admin_email: "", admin_full_name: "", admin_password: "" });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Dialog open={open} onClose={onClose} title="Nueva empresa">
      <div className="space-y-3">
        <Field label="Nombre" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Field
          label="Slug (URL amigable)"
          value={form.slug}
          onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
          placeholder="acme-corp"
        />
        <hr className="my-3 border-slate-200" />
        <div className="text-sm font-medium text-slate-700">Admin inicial de la empresa</div>
        <Field
          label="Email"
          type="email"
          value={form.admin_email}
          onChange={(v) => setForm({ ...form, admin_email: v })}
        />
        <Field
          label="Nombre completo"
          value={form.admin_full_name}
          onChange={(v) => setForm({ ...form, admin_full_name: v })}
        />
        <Field
          label="Contraseña (min 12 chars)"
          type="password"
          value={form.admin_password}
          onChange={(v) => setForm({ ...form, admin_password: v })}
        />
        {error && <div className="rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => createMut.mutate()} disabled={createMut.isPending}>
            {createMut.isPending ? "Creando..." : "Crear empresa"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      <Input value={value} onChange={(e) => onChange(e.target.value)} type={type} placeholder={placeholder} />
    </label>
  );
}
